// System directives

"use client";

// System dependencies

import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Application dependencies

import FormInputPassword from "@/components/form/formInputPassword/FormInputPassword";
import FormInputText from "@/components/form/formInputText/FormInputText";
import FormSpinnerPage, { FormSpinnerPageProps } from "@/components/form/formSpinner/FormSpinnerPage";
import FormThemeSwitcher from "@/components/form/formThemeSwitcher/FormThemeSwitcher";
import NavLink from "@/components/nav/auth/navLink/NavLink";
import { AuthFields, AuthWaitingStatus, AuthWaitingType } from "@/_auth/interfaces/auth.interfaces";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { NAV_HEADER_NAME } from "@/interfaces/nav.interfaces";
import { RouteApis, RouteFields, RouteNames } from "@/interfaces/route.interfaces";
import { SignUpSchema } from "@/schemas/authorisationSchema";
import { clearFormSpinner, concatFields, formatErrorMessage, manageFormSpinner, manageFormSpinnerPage } from "@/lib/functions";
import { getAuthUserByEmailAction } from "@/_auth/actions/auth.actions";
import { supabaseClient } from "@/_auth/utils/supabase/client";
import { toast } from "react-toastify";

// Namespace

// const NAMESPACE = "signup";

// devConsole.info(NAMESPACE,``);

const SignUpPage = () => {
  // Application state

  const [spinnerPageState, setSpinnerPageState] = useState<FormSpinnerPageProps | null>(null);
  const router = useRouter();

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Use effect to handle the form spinner control on component mount
  // ---------------------------------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    clearFormSpinner();
  }, []);

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // React Hook Forms management
  // ---------------------------------------------------------------------------------------------------------------------------------------

  // Set the Form instance and submit handler

  const signUpForm = useForm<z.infer<typeof SignUpSchema>>({
    defaultValues: {
      [AuthFields.FirstName]: "",
      [AuthFields.LastName]: "",
      [AuthFields.Email]: "",
      [AuthFields.Password]: "",
      [AuthFields.ConfirmPassword]: "",
    },
    mode: "onChange",
    resolver: zodResolver(SignUpSchema),
  });

  // Submit record

  const onSignUpSubmit = signUpForm.handleSubmit(async (values: z.infer<typeof SignUpSchema>) => {
    // Run up the spinner.

    manageFormSpinnerPage("attempting to sign up", setSpinnerPageState);

    // Try and sign up with the values entered.

    try {
      // Try and get a user for the email presented.

      const { response } = await getAuthUserByEmailAction(values[AuthFields.Email].toLowerCase());

      // See whether we have an error.

      if (response[AuthFields.Status] !== 200) {
        // To be here there is an error, put the message into a toast, clear the form spinner page and return.

        toast.error(`error experienced - ${response[AuthFields.Message]}`);
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // See whether we have a user for the email presented.

      if (response[AuthFields.Id]) {
        // To be here there is an existing user, put up a toast to that effect, clear the form spinner page and return.

        toast.error("this email already exists, please sign in instead");
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // Set the emailRedirectTo value.

      const emailRedirectTo = `${process.env.NEXT_PUBLIC_APP_URL}/api/auth/callbacks${RouteApis.signup}`;

      // Create a new user using Supabase client side.

      const { error } = await supabaseClient.auth.signUp({
        [AuthFields.Email]: values[AuthFields.Email].toLowerCase(),
        [AuthFields.Password]: values[AuthFields.Password],
        options: {
          data: {
            [AuthFields.FirstName]: values[AuthFields.FirstName],
            [AuthFields.LastName]: values[AuthFields.LastName],
            [AuthFields.FullName]: concatFields(values[AuthFields.FirstName], values[AuthFields.LastName], " "),
          },
          emailRedirectTo,
        },
      });

      // Check to see whether there was an error signing up the user.

      if (error) {
        // We have an error so we can put the toast up, clear the form spinner page and return.

        toast.error(formatErrorMessage(error));
        manageFormSpinnerPage(null, setSpinnerPageState);
        return;
      }

      // To be here we have been successful signing up the user. Put up some toasts confirming the success and letting the user know that
      // there will be an email with a link and the link will spawn a new page.

      toast.success("Your sign-up request has been received and an email has been sent.");
      toast.info("When you click the email link you will be signed in to the system.");

      // Reset the form spinner page.

      manageFormSpinnerPage("loading waiting page", setSpinnerPageState);

      // And push to the waiting page.

      router.push(
        `${RouteNames[RouteFields.Waiting].route}?${AuthFields.Type}=${AuthWaitingType.Signup}&${AuthFields.Status}=${AuthWaitingStatus.Waiting}`,
      );
    } catch (error) {
      // Display a toast with the error.

      toast.error(formatErrorMessage(error));

      // And turn off the spinner.

      manageFormSpinnerPage(null, setSpinnerPageState);
    }
  });

  // ---------------------------------------------------------------------------------------------------------------------------------------
  // Form interaction and rendering
  // ---------------------------------------------------------------------------------------------------------------------------------------

  const signUpReset = () => {
    signUpForm.reset();
  };

  return (
    <div className="relative flex h-full min-h-screen w-full flex-col justify-between bg-white px-4 py-6 dark:bg-slate-800 dark:text-white xsm:px-10 sm:h-full sm:w-full md:h-full md:w-3/5 lg:h-full lg:w-2/5 xl:h-full 3xl:px-7p 4xl:px-10p">
      <div>
        {spinnerPageState && <FormSpinnerPage isActive={spinnerPageState.isActive} caption={spinnerPageState.caption} />}
        {/* Logo */}
        <span onClick={() => manageFormSpinner(`loading ${RouteNames[RouteFields.Home].name} page - please wait`)}>
          <Link className="flex cursor-pointer space-x-3 whitespace-nowrap focus:outline-0" href={RouteNames[RouteFields.Home].route}>
            <Image alt="Logo" className="h-auto w-auto" height={28} src={"assets/logo-icon.svg"} width={28} />
            <span className="self-center whitespace-nowrap text-xl md:text-2xl">
              <span className="text-logoblue dark:text-logoblue-300">{NAV_HEADER_NAME.Prefix}</span>
              <span className="font-semibold">{NAV_HEADER_NAME.Suffix}</span>
            </span>
          </Link>
        </span>
        {/* Content */}
        <div className="flex w-full flex-col">
          <div className="mt-[45px] w-full">
            {/* Heading */}
            <div className="mb-4 flex w-full flex-col">
              <h3 className="mb-2 text-2xl font-semibold">Sign Up</h3>
              <p className="mb-2 text-base">Welcome aboard - please register your details to sign up</p>
            </div>
            {/* Form */}
            <Form {...signUpForm}>
              <form id="signup" noValidate onSubmit={onSignUpSubmit}>
                <div className="flex w-full flex-col">
                  <FormInputText
                    control={signUpForm.control}
                    label="first name"
                    name={AuthFields.FirstName}
                    placeholder="enter first name"
                  />
                </div>
                <div className="mt-2 flex w-full flex-col">
                  <FormInputText control={signUpForm.control} label="last name" name={AuthFields.LastName} placeholder="enter last name" />
                </div>
                <div className="mt-2 flex w-full flex-col">
                  <FormInputText
                    control={signUpForm.control}
                    inputProps={{ style: { textTransform: "lowercase" } }}
                    label="email"
                    name={AuthFields.Email}
                    placeholder="enter email address"
                  />
                </div>
                <div className="mt-2 flex w-full flex-col">
                  <FormInputPassword
                    control={signUpForm.control}
                    label="password"
                    name={AuthFields.Password}
                    placeholder="enter your password"
                  />
                </div>
                <div className="mt-2 flex w-full flex-col">
                  <FormInputPassword
                    control={signUpForm.control}
                    label="confirm password"
                    name={AuthFields.ConfirmPassword}
                    placeholder="confirm your password"
                  />
                </div>
                <div className="flex w-full items-center justify-between pt-2">
                  <NavLink displaySpinner={false} label="Reset" onClick={signUpReset} />
                </div>
                <div className="my-5 flex w-full flex-col">
                  <Button className="w-full" type="submit">
                    Sign up
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </div>
      {/* Footer */}
      <div className="flex w-full items-center justify-between">
        <span className="flex text-sm font-normal text-gray-700 dark:text-gray-400">
          {"Already have an account?"}
          <span className="ml-1">
            <NavLink
              displaySpinner={true}
              href={RouteNames[RouteFields.SignIn].route}
              label="Sign in here"
              pageName={RouteNames[RouteFields.SignIn].name}
            />
          </span>
        </span>
        <FormThemeSwitcher />
      </div>
    </div>
  );
};

export default SignUpPage;
